import React, { useCallback, useEffect, useState } from "react";
import LoadError from '../../components/admin/LoadError';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tab,
  Tabs,
  Chip,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  MenuItem,
  TextField as MuiTextField,
  Divider,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import InsightsIcon from "@mui/icons-material/Insights";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api";
import { adminHasPermission } from "../../utils/adminPermissions";
import RefundTransactionDialog, { RefundStateChip } from "./RefundTransactionDialog";

const statusColor = (status) => {
  switch (status) {
    case "active":
    case "completed":
    case "paid":
      return "success";
    case "trial":
      return "info";
    case "pending":
    case "trial_ended_pending":
      return "warning";
    case "expired":
    case "cancelled":
      return "default";
    case "failed":
      return "error";
    default:
      return "default";
  }
};

const STATUS_LABELS = {
  trial_ended_pending: "Trial ended",
  none: "No plan",
};

const statusLabel = (status) => {
  if (!status) return "No plan";
  if (STATUS_LABELS[status]) return STATUS_LABELS[status];
  const text = String(status).replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString('en-GB', { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return value;
  }
};

const formatDay = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString('en-GB', { dateStyle: "medium" });
  } catch {
    return value;
  }
};

const formatPaymentMethod = (method, isAutomatic = false) => {
  if (!method) return "—";
  if (method === "bank_transfer") return "Bank transfer";
  if (method === "stripe") return isAutomatic ? "Stripe auto-charge" : "Stripe";
  return method;
};

const formatPlatform = (platform) => {
  if (!platform) return "—";
  const value = String(platform).toLowerCase();
  if (value === "ios") return "iOS";
  if (value === "android") return "Android";
  if (value === "web") return "Web";
  return platform;
};

const formatMoney = (pence, currency = "gbp") => {
  const amount = Number(pence || 0) / 100;
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: (currency || "gbp").toUpperCase(),
    }).format(amount);
  } catch {
    return `£${amount.toFixed(2)}`;
  }
};

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
  { value: "trial_ended_pending", label: "Trial ended" },
  { value: "past_due", label: "Past due" },
  { value: "expired", label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
  { value: "inactive", label: "Expired / ended / cancelled" },
  { value: "none", label: "No plan" },
];

// The dashboard's Subscriptions segments: each user's latest subscription, so the list
// matches the number the dashboard showed.
const SUBSCRIPTION_STATE_OPTIONS = [
  { value: "", label: "Any subscription" },
  { value: "free", label: "Free trial" },
  { value: "trial", label: "Trial" },
  { value: "paid", label: "Paid" },
  { value: "expired", label: "Expired" },
];

const PLAN_OPTIONS = [
  { value: "", label: "All plans" },
  { value: "free-trial-7-days", label: "Free (7-day trial)" },
  { value: "trial-weekly", label: "Trial (weekly)" },
  { value: "standard-monthly", label: "Standard (monthly)" },
];

const PLATFORM_OPTIONS = [
  { value: "", label: "All platforms" },
  { value: "ios", label: "iOS" },
  { value: "android", label: "Android" },
  { value: "web", label: "Web" },
  { value: "unknown", label: "Unknown" },
];

const TRIAL_REMINDER_OPTIONS = [
  { value: "", label: "Any trial reminder" },
  { value: "sent", label: "Reminder sent" },
  { value: "not_sent", label: "Reminder not sent" },
];

const SORT_OPTIONS = [
  { value: "", label: "Newest first" },
  { value: "opens", label: "Most app opens" },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "succeeded", label: "Completed or paid" },
  { value: "completed", label: "Completed" },
  { value: "paid", label: "Paid" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: "", label: "All methods" },
  { value: "stripe", label: "Stripe" },
  { value: "bank_transfer", label: "Bank transfer" },
];

const FILTER_KEYS = ["state", "status", "plan", "platform", "trial_reminder", "sort", "from", "to", "active_from", "active_to", "payment_status", "payment_method", "paid_from", "paid_to"];

const HEAD_CELL_SX = {
  fontWeight: 800,
  color: "#111827",
  bgcolor: "#f9fafb",
  whiteSpace: "nowrap",
};

const CELL_SX = { color: "#374151" };

const MobileUsers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const param = (key) => searchParams.get(key) || "";

  const tab = param("tab") || "students";
  const subscriptionState = param("state");
  const status = param("status");
  const plan = param("plan");
  const platform = param("platform");
  const sort = param("sort");
  const trialReminder = param("trial_reminder");
  const from = param("from");
  const to = param("to");
  const activeFrom = param("active_from");
  const activeTo = param("active_to");
  const paidFrom = param("paid_from");
  const paidTo = param("paid_to");
  const paymentStatus = param("payment_status");
  const paymentMethod = param("payment_method");
  const appliedSearch = param("search");
  const hasFilters = FILTER_KEYS.some((key) => param(key));

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [refundRow, setRefundRow] = useState(null);
  const canRefund = adminHasPermission("refund-transactions");
  const [paymentPage, setPaymentPage] = useState(0);
  const [paymentRowsPerPage, setPaymentRowsPerPage] = useState(20);
  const [paymentTotal, setPaymentTotal] = useState(0);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await api.get("admin/mobile-users", {
        params: {
          page: page + 1,
          per_page: rowsPerPage,
          search: appliedSearch.trim() || undefined,
          state: subscriptionState || undefined,
          status: status || undefined,
          plan: plan || undefined,
          platform: platform || undefined,
          sort: sort || undefined,
          trial_reminder: trialReminder || undefined,
          from: from || undefined,
          to: to || undefined,
          active_from: activeFrom || undefined,
          active_to: activeTo || undefined,
        },
      });
      if (res.data?.success) {
        const payload = res.data.data || {};
        setRows(payload.data || []);
        setTotal(payload.total || 0);
      }
    } catch (e) {
      setLoadError(e || true);
      toast.error(e?.response?.data?.message || "Failed to load mobile users.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, appliedSearch, subscriptionState, status, plan, platform, trialReminder, sort, from, to, activeFrom, activeTo]);

  const fetchPayments = useCallback(async () => {
    try {
      setPaymentsLoading(true);
      const res = await api.get("admin/mobile-users/payments", {
        params: {
          page: paymentPage + 1,
          per_page: paymentRowsPerPage,
          search: appliedSearch.trim() || undefined,
          payment_status: paymentStatus || undefined,
          payment_method: paymentMethod || undefined,
          paid_from: paidFrom || undefined,
          paid_to: paidTo || undefined,
        },
      });
      if (res.data?.success) {
        const payload = res.data.data || {};
        setPayments(payload.data || []);
        setPaymentTotal(payload.total || 0);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load mobile payments.");
      setPayments([]);
      setPaymentTotal(0);
    } finally {
      setPaymentsLoading(false);
    }
  }, [paymentPage, paymentRowsPerPage, appliedSearch, paymentStatus, paymentMethod, paidFrom, paidTo]);

  // The URL is the source of truth so every dashboard drill-through deep link works and
  // the view stays shareable; typing is debounced into it rather than into local state.
  const setParams = useCallback((patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next, { replace: true });
    setPage(0);
    setPaymentPage(0);
  }, [searchParams, setSearchParams]);

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    FILTER_KEYS.forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
    setPage(0);
    setPaymentPage(0);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if ((searchParams.get("search") || "") !== search) setParams({ search });
    }, 300);
    return () => clearTimeout(timer);
  }, [search, searchParams, setParams]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const studentsTable = (
    <>
      {loadError && !loading && <LoadError what="mobile users" error={loadError} onRetry={() => fetchRows()} />}
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_CELL_SX}>ID</TableCell>
              <TableCell sx={HEAD_CELL_SX}>User</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Platform</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Plan</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
              <TableCell sx={{ ...HEAD_CELL_SX, textAlign: "right" }}>App opens</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Last active</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Next billing</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Trial reminder sent</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Last payment</TableCell>
              <TableCell sx={{ ...HEAD_CELL_SX, textAlign: "right" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 6, color: "#6b7280" }}>
                  {appliedSearch || hasFilters ? "No mobile users match these filters." : "No mobile users found."}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover sx={{ "&:nth-of-type(odd)": { bgcolor: "#fcfcfd" } }}>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 700 }}>{row.id}</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#111827" }}>
                      {row.full_name || "—"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#9aa1ac", wordBreak: "break-all" }}>
                      {row.email || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={CELL_SX}>
                    {formatPlatform(row.platform)}
                    {row.country ? (
                      <Typography variant="caption" display="block" sx={{ color: "#9aa1ac" }}>
                        {row.country}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, whiteSpace: "nowrap" }}>{row.plan_name || "—"}</TableCell>
                  <TableCell>
                    {/* status stays "active" after auto-renew is turned off; say so instead. */}
                    <Chip
                      size="small"
                      label={row.auto_renew_off ? "Auto-renew off" : statusLabel(row.subscription_status)}
                      color={row.auto_renew_off ? "warning" : statusColor(row.subscription_status)}
                    />
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 700, textAlign: "right" }}>
                    {row.app_opens ? row.app_opens.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "—"}
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, whiteSpace: "nowrap" }}>{formatDateTime(row.app_last_active_at)}</TableCell>
                  <TableCell sx={{ ...CELL_SX, whiteSpace: "nowrap" }}>
                    {row.auto_renew_off && row.next_billing_date
                      ? `Ends ${formatDay(row.next_billing_date)}`
                      : formatDay(row.next_billing_date)}
                    {row.pending_plan_name && !row.auto_renew_off ? (
                      <Typography variant="caption" display="block" sx={{ color: "#9aa1ac" }}>
                        then {row.pending_plan_name}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, whiteSpace: "nowrap" }}>
                    {row.trial_reminder_sent_at ? formatDateTime(row.trial_reminder_sent_at) : "—"}
                    {row.trial_ends_at ? (
                      <Typography variant="caption" display="block" sx={{ color: "#9aa1ac" }}>
                        Trial ends {formatDay(row.trial_ends_at)}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ minWidth: 190 }}>
                    {row.last_payment_status ? (
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Chip
                            size="small"
                            label={statusLabel(row.last_payment_status)}
                            color={statusColor(row.last_payment_status)}
                          />
                          {row.last_payment_amount_pence != null ? (
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#374151" }}>
                              {formatMoney(row.last_payment_amount_pence, row.last_payment_currency)}
                            </Typography>
                          ) : null}
                        </Stack>
                        <Typography variant="caption" sx={{ color: "#9aa1ac" }}>
                          {[
                            formatPaymentMethod(row.last_payment_method, row.last_payment_is_automatic),
                            row.last_payment_platform,
                            row.last_payment_paid_at ? formatDateTime(row.last_payment_paid_at) : null,
                          ]
                            .filter((part) => part && part !== "—")
                            .join(" · ") || "—"}
                        </Typography>
                        {row.last_payment_admin_notes ? (
                          <Typography
                            variant="caption"
                            color={row.last_payment_status === "failed" ? "error" : "text.secondary"}
                          >
                            {row.last_payment_admin_notes}
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography variant="body2" sx={{ color: "#9aa1ac" }}>No payments</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: "none", fontWeight: 700 }}
                        onClick={() => navigate(`/admin/mobile-users/${row.id}`)}
                      >
                        View
                      </Button>
                      <Tooltip title="Readiness, subjects and worksheet attempts">
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          startIcon={<InsightsIcon />}
                          sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
                          onClick={() => navigate(`/admin/mobile-users/${row.id}/progress`)}
                        >
                          Progress
                        </Button>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50]}
      />
    </>
  );

  const paymentsTable = (
    <>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_CELL_SX}>Date</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Student</TableCell>
              <TableCell sx={{ ...HEAD_CELL_SX, textAlign: "right" }}>Amount</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Method</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Platform</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Plan</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Notes</TableCell>
              {canRefund && <TableCell sx={{ ...HEAD_CELL_SX, textAlign: "right" }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canRefund ? 9 : 8} align="center" sx={{ py: 6, color: "#6b7280" }}>
                  {appliedSearch || hasFilters ? "No payments match these filters." : "No payments recorded yet."}
                </TableCell>
              </TableRow>
            ) : (
              payments.map((payment) => (
                <TableRow key={payment.id} hover sx={{ "&:nth-of-type(odd)": { bgcolor: "#fcfcfd" } }}>
                  <TableCell sx={{ ...CELL_SX, whiteSpace: "nowrap" }}>
                    {formatDateTime(payment.paid_at || payment.created_at)}
                    {!payment.paid_at && payment.created_at ? (
                      <Typography variant="caption" display="block" sx={{ color: "#9aa1ac" }}>
                        attempted
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#111827" }}>
                      {payment.user_name || "—"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#9aa1ac", wordBreak: "break-all" }}>
                      {payment.user_email || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ ...CELL_SX, fontWeight: 700, whiteSpace: "nowrap" }}>
                    {formatMoney(payment.amount_pence, payment.currency)}
                  </TableCell>
                  <TableCell>
                    {payment.refund_state && payment.refund_state !== "none" ? (
                      <RefundStateChip row={payment} />
                    ) : (
                      <Chip size="small" label={statusLabel(payment.status)} color={statusColor(payment.status)} />
                    )}
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, whiteSpace: "nowrap" }}>
                    {formatPaymentMethod(payment.payment_method, payment.is_automatic)}
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, whiteSpace: "nowrap" }}>
                    {payment.payment_platform || "—"}
                  </TableCell>
                  <TableCell sx={CELL_SX}>{payment.plan_name || "—"}</TableCell>
                  <TableCell sx={CELL_SX}>{payment.admin_notes || payment.receipt_reference || "—"}</TableCell>
                  {canRefund && (
                    <TableCell align="right">
                      {payment.refundable || (payment.refund_state && payment.refund_state !== "none") ? (
                        <Tooltip title={payment.refundable ? "Refund" : "Refund history / revoke access"} arrow>
                          <IconButton
                            size="small" color={payment.refundable ? "error" : "default"}
                            onClick={() => setRefundRow({
                              purchasable_type: payment.purchasable_type,
                              purchase_id: payment.id,
                              student_name: payment.user_name,
                              student_email: payment.user_email,
                              product: `Mobile app${payment.plan_name ? ` – ${payment.plan_name}` : ""}`,
                              currency: payment.currency,
                            })}
                          >
                            <CurrencyExchangeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : payment.status !== "failed" ? (
                        <Tooltip
                          title={payment.payment_method === "stripe"
                            ? "No Stripe payment on record for this charge"
                            : "Refund through the App Store / Play Console or bank"}
                          arrow
                        >
                          <span>
                            <IconButton size="small" disabled><CurrencyExchangeIcon fontSize="small" /></IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={paymentTotal}
        page={paymentPage}
        onPageChange={(_, next) => setPaymentPage(next)}
        rowsPerPage={paymentRowsPerPage}
        onRowsPerPageChange={(e) => {
          setPaymentRowsPerPage(parseInt(e.target.value, 10));
          setPaymentPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50]}
      />
      <RefundTransactionDialog
        open={!!refundRow}
        row={refundRow}
        onClose={() => setRefundRow(null)}
        onDone={fetchPayments}
      />
    </>
  );

  const activeLoading = tab === "students" ? loading : paymentsLoading;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <PhoneIphoneIcon color="primary" />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Mobile app users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Student accounts plus Stripe charges, including auto-renewals and failed cards.
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, email, pet name, or user id"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          helperText="Filters both students and payments."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
          {tab === "students" ? (
            <>
              <MuiTextField
                select size="small" label="Subscription" value={subscriptionState}
                onChange={(e) => setParams({ state: e.target.value })}
                sx={{ minWidth: 170 }}
              >
                {SUBSCRIPTION_STATE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </MuiTextField>
              <MuiTextField
                select size="small" label="Status" value={status}
                onChange={(e) => setParams({ status: e.target.value })}
                sx={{ minWidth: 160 }}
              >
                {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </MuiTextField>
              <MuiTextField
                select size="small" label="Plan" value={plan}
                onChange={(e) => setParams({ plan: e.target.value })}
                sx={{ minWidth: 190 }}
              >
                {PLAN_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </MuiTextField>
              <MuiTextField
                select size="small" label="Platform" value={platform}
                onChange={(e) => setParams({ platform: e.target.value })}
                sx={{ minWidth: 150 }}
              >
                {PLATFORM_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </MuiTextField>
              <MuiTextField
                select size="small" label="Sort" value={sort}
                onChange={(e) => setParams({ sort: e.target.value })}
                sx={{ minWidth: 160 }}
              >
                {SORT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </MuiTextField>
              <MuiTextField
                select size="small" label="Trial reminder" value={trialReminder}
                onChange={(e) => setParams({ trial_reminder: e.target.value })}
                sx={{ minWidth: 180 }}
              >
                {TRIAL_REMINDER_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </MuiTextField>
              <MuiTextField
                size="small" type="date" label="Joined from" value={from}
                onChange={(e) => setParams({ from: e.target.value })}
                InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
              />
              <MuiTextField
                size="small" type="date" label="Joined to" value={to}
                onChange={(e) => setParams({ to: e.target.value })}
                InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
              />
              {/* Opened the app in this window (dashboard "Active users"). */}
              <MuiTextField
                size="small" type="date" label="Active from" value={activeFrom}
                onChange={(e) => setParams({ active_from: e.target.value })}
                InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
              />
              <MuiTextField
                size="small" type="date" label="Active to" value={activeTo}
                onChange={(e) => setParams({ active_to: e.target.value })}
                InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
              />
            </>
          ) : (
            <>
              <MuiTextField
                select size="small" label="Payment status" value={paymentStatus}
                onChange={(e) => setParams({ payment_status: e.target.value })}
                sx={{ minWidth: 180 }}
              >
                {PAYMENT_STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </MuiTextField>
              <MuiTextField
                select size="small" label="Method" value={paymentMethod}
                onChange={(e) => setParams({ payment_method: e.target.value })}
                sx={{ minWidth: 180 }}
              >
                {PAYMENT_METHOD_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </MuiTextField>
              <MuiTextField
                size="small" type="date" label="Paid from" value={paidFrom}
                onChange={(e) => setParams({ paid_from: e.target.value })}
                InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
              />
              <MuiTextField
                size="small" type="date" label="Paid to" value={paidTo}
                onChange={(e) => setParams({ paid_to: e.target.value })}
                InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
              />
            </>
          )}
          {hasFilters && (
            <Button size="small" onClick={clearFilters} sx={{ textTransform: "none", fontWeight: 700 }}>
              Clear filters
            </Button>
          )}
        </Stack>
      </Paper>

      <Paper>
        <Tabs
          value={tab}
          onChange={(_, next) => setParams({ tab: next === "students" ? "" : next })}
          sx={{ px: 2, borderBottom: "1px solid #eef0f4", "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
        >
          <Tab value="students" label={`Students (${total})`} />
          <Tab value="payments" label={`Payments (${paymentTotal})`} />
        </Tabs>

        {activeLoading ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : tab === "students" ? (
          studentsTable
        ) : (
          paymentsTable
        )}
      </Paper>
    </Box>
  );
};

export default MobileUsers;
